import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useLocation } from '@/shared/hooks/useLocation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useToast } from '@/shared/ui/Toast';
import { extractionApi, type Phase1Data, type MembershipPlan } from '../api/extractionApi';
import { strategyApi, type StrategyQuestion, type StrategyResponse } from '../api/strategyApi';
import {
  locationsApi,
  type LocationData,
  type LocationInput,
  type LocationProfile,
  type LocationProfileInput,
  type DayHours,
} from '@/modules/settings/api/locationsApi';
import type { OfficeHours } from '../api/extractionApi';
import { strategyKeys } from '../queryKeys';
import { settingsKeys } from '@/modules/settings/queryKeys';
import type { Pick as BrandPick } from '../constants/brandQuestions';
import { Button, Callout, EmptyState, SectionLabel } from '@/shared/ui';
import { isDirty } from '@/shared/utils/isDirty';
import { formatOfficeHours } from '../utils/officeHoursHelpers';
import { FIELDS, parseCSV, parseAddressString } from '../constants/profileFields';
import { STRATEGY_SECTIONS, ARCHETYPE_Q_KEYS } from '../constants/strategySections';
import type { SectionKey } from '../constants/strategySections';
import { VapiCallModal } from '../components/VapiCallModal';
import { VapiCallBanner } from '../components/VapiCallBanner';
import { PracticeDataSection } from './PracticeDataSection';
import type { FieldState } from './PracticeDataSection';
import { StrategicInputsSection } from './StrategicInputsSection';
import { BrandPositioningSection } from './BrandPositioningSection';
import styles from './PracticeProfileStep.module.css';

const isEmptyAnswer = (v: unknown) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/**
 * The response writes needed to bring the server in line with local answers:
 * changed answers are saved, and an answer the user cleared is sent as null
 * so the server deletes it (otherwise it would come back after a reload).
 */
function dirtyUpdates(
  questionIds: string[],
  responses: Record<string, unknown>,
  saved: Record<string, unknown>,
): Array<{ questionId: string; response: unknown }> {
  const updates: Array<{ questionId: string; response: unknown }> = [];
  for (const questionId of questionIds) {
    const v = responses[questionId];
    if (isEmptyAnswer(v)) {
      if (!isEmptyAnswer(saved[questionId])) updates.push({ questionId, response: null });
      continue;
    }
    if (isDirty(saved[questionId], v)) updates.push({ questionId, response: v });
  }
  return updates;
}

export function PracticeProfileStep() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const { user } = useAuth();
  const [vapiModalOpen, setVapiModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { success: toastSuccess, error: toastError } = useToast();
  const { registerOnBeforeNext, registerNextDisabled, reportStepReached } = useOutletContext<{
    registerOnBeforeNext: (fn: (() => void) | null) => void;
    registerNextDisabled: (disabled: boolean) => void;
    reportStepReached: (step: number) => void;
  }>();
  const [searchParams] = useSearchParams();

  // ── Data fetching ──
  const extractionQuery = useQuery({
    queryKey: strategyKeys.extraction(locationId),
    queryFn: () => extractionApi.get(locationId),
    enabled: !!locationId,
  });

  const questionsQuery = useQuery({
    queryKey: strategyKeys.questions(locationId),
    queryFn: () => strategyApi.getQuestions(locationId),
    enabled: !!locationId,
  });

  const responsesQuery = useQuery({
    queryKey: strategyKeys.responses(locationId),
    queryFn: () => strategyApi.getResponses(locationId),
    enabled: !!locationId,
  });

  const locationsQuery = useQuery({
    queryKey: settingsKeys.locations(),
    queryFn: () => locationsApi.getAll(),
    enabled: !!locationId,
  });

  const locationProfileQuery = useQuery({
    queryKey: settingsKeys.locationProfile(locationId),
    queryFn: () => locationsApi.getProfile(locationId),
    enabled: !!locationId,
  });

  const vapiCallsQuery = useQuery({
    queryKey: strategyKeys.vapiCalls(user?.practiceId ?? ''),
    queryFn: () => strategyApi.getVapiCalls(user!.practiceId!),
    enabled: !!user?.practiceId,
  });

  // ── Local editing state ──
  const [fields, setFields] = useState<FieldState>({});
  const [initialized, setInitialized] = useState(false);
  const [officeHoursLocal, setOfficeHoursLocal] = useState<OfficeHours>({});
  const [membershipPlansLocal, setMembershipPlansLocal] = useState<MembershipPlan[]>([]);
  const [responses, setResponses] = useState<Record<string, unknown>>({});
  const [savedResponseMap, setSavedResponseMap] = useState<Record<string, unknown>>({});
  const [confirmedSections, setConfirmedSections] = useState<Set<string>>(new Set());
  const [picks, setPicks] = useState<BrandPick[]>(Array(8).fill(null));

  // Section 1 expands to show fields; collapses to summary card when confirmed
  const [section1Expanded, setSection1Expanded] = useState(true);
  // Which sub-section is open (strategy sections or 'brand')
  const [openSubSection, setOpenSubSection] = useState<string | null>(null);
  const subSectionHeaderRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingScrollToKey = useRef<string | null>(null);

  // ── Canonical data projection ──
  const extraction = extractionQuery.data?.data;
  const extracted: Phase1Data = extraction?.extracted ?? {};
  // Confirming sections creates an empty extraction row, so "has the website
  // been read" is about its content, not whether the row exists.
  const websiteRead = Object.keys(extracted).length > 0;
  const location: LocationData | undefined = locationsQuery.data?.data.find(
    (l) => l.id === locationId,
  );
  const locationProfile: LocationProfile | null = locationProfileQuery.data?.data ?? null;

  const canonical: Phase1Data = {
    ...(location?.phone ? { phones: [location.phone] } : {}),
    ...(location?.address ? { addresses: [location.address] } : {}),
    ...(location?.address || location?.city || location?.state || location?.zip
      ? {
          structuredAddress: {
            street: location?.address,
            city: location?.city,
            state: location?.state,
            zip: location?.zip,
            full: [location?.address, location?.city, location?.state, location?.zip]
              .filter(Boolean)
              .join(', '),
          },
        }
      : {}),
    ...(locationProfile?.services?.length ? { services: locationProfile.services } : {}),
    ...(locationProfile?.providers?.length ? { providers: locationProfile.providers } : {}),
    ...(locationProfile?.technology?.length ? { technology: locationProfile.technology } : {}),
    ...(locationProfile?.insurances?.length ? { insurances: locationProfile.insurances } : {}),
    ...(locationProfile?.languages?.length ? { languages: locationProfile.languages } : {}),
    ...(locationProfile?.membershipPlans != null
      ? { membershipPlans: locationProfile.membershipPlans as MembershipPlan[] }
      : {}),
    ...(locationProfile?.paymentOptions != null
      ? { paymentOptions: locationProfile.paymentOptions as string[] }
      : {}),
    ...(locationProfile?.officeHours ? { officeHours: locationProfile.officeHours } : {}),
  };

  const phase1: Phase1Data = { ...extracted, ...canonical };

  // ── Questions split by section ──
  const questions: StrategyQuestion[] = questionsQuery.data?.data ?? [];
  const savedResponses: StrategyResponse[] = responsesQuery.data?.data ?? [];

  const allStrategyQuestions = [...questions]
    .filter((q) => q.section !== 'Brand & Positioning' && q.section !== 'Your Brand')
    .sort((a, b) => a.order - b.order);

  const growthQuestions = allStrategyQuestions.filter((q) =>
    (STRATEGY_SECTIONS[0].dbSections as readonly string[]).includes(q.section),
  );
  const patientsQuestions = allStrategyQuestions.filter((q) =>
    (STRATEGY_SECTIONS[1].dbSections as readonly string[]).includes(q.section),
  );
  const budgetQuestions = allStrategyQuestions.filter((q) =>
    (STRATEGY_SECTIONS[2].dbSections as readonly string[]).includes(q.section),
  );

  const yourBrandQuestions = useMemo(
    () => questions.filter((q) => q.section === 'Your Brand').sort((a, b) => a.order - b.order),
    [questions],
  );

  const archetypeQuestionIds = useMemo(
    () =>
      Object.fromEntries(
        ARCHETYPE_Q_KEYS.map((key) => [key, questions.find((q) => q.questionKey === key)?.id]),
      ) as Record<string, string | undefined>,
    [questions],
  );

  // Maps each questionId to the section key that owns it, for un-confirming on edit
  const questionToSection = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of growthQuestions) map.set(q.id, 'growth');
    for (const q of patientsQuestions) map.set(q.id, 'patients');
    for (const q of budgetQuestions) map.set(q.id, 'budget');
    for (const q of yourBrandQuestions) map.set(q.id, 'brand');
    for (const qId of Object.values(archetypeQuestionIds)) {
      if (qId) map.set(qId, 'brand');
    }
    return map;
  }, [
    growthQuestions,
    patientsQuestions,
    budgetQuestions,
    yourBrandQuestions,
    archetypeQuestionIds,
  ]);

  const questionToSectionKeys = useMemo(
    () => new Set(questionToSection.values()),
    [questionToSection],
  );

  // ── VAPI call for this location ──
  const locationVapiCall = (vapiCallsQuery.data?.data ?? [])
    .filter((c) => c.locationId === locationId && c.status !== 'scheduled')
    .sort((a, b) => new Date(b.processedAt).getTime() - new Date(a.processedAt).getTime())[0];

  // ── Initialize field state ──
  useEffect(() => {
    if (initialized) return;
    if (!extractionQuery.data || !responsesQuery.data) return;
    if (!locationsQuery.data || !locationProfileQuery.data) return;

    setOfficeHoursLocal(phase1.officeHours ?? {});
    setMembershipPlansLocal(phase1.membershipPlans ?? []);

    const init: FieldState = {};
    for (const f of FIELDS) {
      const isConfirmed = f.hasValue ? f.hasValue(canonical) : false;
      init[f.key] = { value: f.getValue(phase1), confirmed: isConfirmed, editing: false };
    }
    setFields(init);

    const initResp: Record<string, unknown> = {};
    for (const r of savedResponses) initResp[r.questionId] = r.response;
    setResponses(initResp);
    setSavedResponseMap(initResp);

    const initPicks = ARCHETYPE_Q_KEYS.map((key) => {
      const r = savedResponses.find((res) => res.question.questionKey === key);
      if (!r) return null;
      const v = r.response;
      return v === 0 || v === 1 ? (v as BrandPick) : null;
    });
    setPicks(initPicks);

    if (extraction?.confirmedSections?.length) {
      setConfirmedSections(new Set(extraction.confirmedSections as string[]));
    }

    setInitialized(true);
  }, [
    extractionQuery.data,
    responsesQuery.data,
    locationsQuery.data,
    locationProfileQuery.data,
    initialized,
  ]);

  // Answers can arrive after the page loaded — most often from an intake call
  // that finishes while the owner waits here. Adopt fresh server answers for
  // every question the owner hasn't edited locally, so captured answers show
  // up without a reload and without clobbering in-progress edits.
  const lastSyncedResponses = useRef(responsesQuery.data);
  useEffect(() => {
    if (!initialized || !responsesQuery.data) return;
    if (lastSyncedResponses.current === responsesQuery.data) return;
    lastSyncedResponses.current = responsesQuery.data;

    const server: Record<string, unknown> = {};
    for (const r of savedResponses) server[r.questionId] = r.response;

    setResponses((prev) => {
      const next = { ...prev };
      for (const [qId, value] of Object.entries(server)) {
        const editedLocally = isDirty(savedResponseMap[qId], prev[qId]);
        if (!editedLocally) next[qId] = value;
      }
      return next;
    });
    setPicks((prev) =>
      ARCHETYPE_Q_KEYS.map((key, i) => {
        const qId = archetypeQuestionIds[key];
        if (!qId || prev[i] !== (savedResponseMap[qId] ?? null)) return prev[i];
        const v = server[qId];
        return v === 0 || v === 1 ? (v as BrandPick) : prev[i];
      }),
    );
    setSavedResponseMap((prev) => ({ ...prev, ...server }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per server snapshot
  }, [responsesQuery.data, initialized]);

  // A new website read (the "Read my website" button) arrives after the page
  // initialised: fill it into every field the owner hasn't confirmed or is
  // editing. Answers and anything already confirmed are left alone.
  const lastExtractedAt = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!initialized) {
      lastExtractedAt.current = extraction?.extractedAt;
      return;
    }
    if (!websiteRead || extraction?.extractedAt === lastExtractedAt.current) return;
    lastExtractedAt.current = extraction?.extractedAt;
    setFields((prev) => {
      const next = { ...prev };
      for (const f of FIELDS) {
        const current = prev[f.key];
        if (current?.confirmed || current?.editing) continue;
        next[f.key] = { value: f.getValue(phase1), confirmed: false, editing: false };
      }
      return next;
    });
    if (!Object.keys(officeHoursLocal).length && phase1.officeHours) {
      setOfficeHoursLocal(phase1.officeHours);
    }
    if (!membershipPlansLocal.length && phase1.membershipPlans?.length) {
      setMembershipPlansLocal(phase1.membershipPlans);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per website read
  }, [extraction?.extractedAt, initialized]);

  // ── Mutations ──
  const confirmMutation = useMutation({
    mutationFn: async (draft: Phase1Data) => {
      await extractionApi.confirm(locationId, draft);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.extraction(locationId) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.locations() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.locationProfile(locationId) });
      toastSuccess('Profile confirmed');
    },
    onError: (err: unknown) =>
      toastError((err as Error).message || 'Failed to save. Please try again.'),
  });

  const runExtractionMutation = useMutation({
    mutationFn: () => extractionApi.run(locationId),
    onSuccess: (res) => {
      // Put the new read in the cache now: re-initialising from the old
      // cached row (as before) left the fields blank until a reload. The
      // effect below fills in the fields the owner hasn't confirmed or edited.
      queryClient.setQueryData(strategyKeys.extraction(locationId), res);
      queryClient.invalidateQueries({ queryKey: strategyKeys.extraction(locationId) });
      toastSuccess('Website details loaded');
    },
    onError: (err: unknown) =>
      toastError((err as Error).message || "Couldn't read your website. Please try again."),
  });

  const saveConfirmedSectionsMutation = useMutation({
    mutationFn: ({ sections, remove }: { sections: string[]; remove?: string[] }) =>
      extractionApi.saveConfirmedSections(locationId, sections, remove),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.extraction(locationId) });
    },
  });

  const saveResponsesMutation = useMutation({
    mutationFn: (updates: Array<{ questionId: string; response: unknown }>) =>
      strategyApi.saveResponses(locationId, updates),
    onSuccess: (_data, updates) => {
      setSavedResponseMap((prev) => {
        const next = { ...prev };
        for (const u of updates) {
          if (u.response === null) delete next[u.questionId];
          else next[u.questionId] = u.response;
        }
        return next;
      });
      queryClient.invalidateQueries({ queryKey: strategyKeys.responses(locationId) });
      toastSuccess('Responses saved');
    },
    onError: () => toastError('Failed to save responses.'),
  });

  const unconfirmSection = (sectionKey: string) => {
    if (!confirmedSections.has(sectionKey)) return;
    setConfirmedSections((prev) => {
      const next = new Set(prev);
      next.delete(sectionKey);
      return next;
    });
    // Persist it too, so an edited section doesn't come back "Confirmed"
    // after a reload with an answer the user hasn't re-confirmed.
    saveConfirmedSectionsMutation.mutate({ sections: [], remove: [sectionKey] });
  };

  const setResponse = (questionId: string, value: unknown) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
    const sectionKey = questionToSection.get(questionId);
    if (sectionKey) unconfirmSection(sectionKey);
  };

  const togglePick = (i: number, j: 0 | 1) => {
    setPicks((prev) => {
      const next = [...prev] as BrandPick[];
      next[i] = j;
      return next;
    });
    unconfirmSection('brand');
  };

  const saveAllResponses = useCallback(async () => {
    // A detail still open for editing isn't saved by leaving the step; say
    // so rather than drop it.
    const editingField = FIELDS.find((f) => fields[f.key]?.editing);
    if (editingField) {
      toastError(`Save or cancel your change to ${editingField.label} first.`);
      throw new Error('Unsaved profile field');
    }
    const updates = dirtyUpdates(Object.keys(responses), responses, savedResponseMap);
    // Also save dirty archetype picks
    ARCHETYPE_Q_KEYS.forEach((key, i) => {
      const qId = archetypeQuestionIds[key];
      if (qId && picks[i] !== null && picks[i] !== savedResponseMap[qId]) {
        updates.push({ questionId: qId, response: picks[i] });
      }
    });
    if (updates.length) await saveResponsesMutation.mutateAsync(updates);
  }, [
    fields,
    toastError,
    responses,
    savedResponseMap,
    picks,
    archetypeQuestionIds,
    saveResponsesMutation,
  ]);

  const saveAllRef = useRef(saveAllResponses);
  saveAllRef.current = saveAllResponses;

  useEffect(() => {
    registerOnBeforeNext(() => saveAllRef.current());
    return () => registerOnBeforeNext(null);
  }, [registerOnBeforeNext]);

  // ── Derived (hoisted so the next effect can read them) ──
  const confirmedCount = Object.values(fields).filter((f) => f.confirmed).length;
  const totalFields = FIELDS.length;
  const section1Done = confirmedCount === totalFields || extraction?.status === 'confirmed';
  // A section with no questions for this vertical has nothing to confirm.
  const section2Done = [
    { key: STRATEGY_SECTIONS[0].key, questions: growthQuestions },
    { key: STRATEGY_SECTIONS[1].key, questions: patientsQuestions },
    { key: STRATEGY_SECTIONS[2].key, questions: budgetQuestions },
  ].every((s) => s.questions.length === 0 || confirmedSections.has(s.key));
  const brandSectionDone = confirmedSections.has('brand');

  useEffect(() => {
    registerNextDisabled(!section1Done || !section2Done || !brandSectionDone);
    return () => registerNextDisabled(false);
  }, [section1Done, section2Done, brandSectionDone, registerNextDisabled]);

  // Advance the saved wizard step to 2 (Practice Data) as soon as all sections
  // are confirmed, without navigating. This keeps the Overview "Step Progress"
  // accurate even if the user never clicks Next.
  useEffect(() => {
    if (section1Done && section2Done && brandSectionDone) {
      reportStepReached(2);
    }
  }, [section1Done, section2Done, brandSectionDone, reportStepReached]);

  // When a sub-section opens, scroll its header to just below the sticky nav.
  useLayoutEffect(() => {
    if (!pendingScrollToKey.current) return;
    const key = pendingScrollToKey.current;
    pendingScrollToKey.current = null;
    const el = subSectionHeaderRefs.current[key];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < 80) {
      window.scrollTo({ top: rect.top + window.scrollY - 80, behavior: 'smooth' });
    }
  });

  // Open brand sub-section when navigated here with ?section=brand
  useEffect(() => {
    if (!initialized) return;
    if (searchParams.get('section') === 'brand') {
      setOpenSubSection('brand');
      setTimeout(() => {
        subSectionHeaderRefs.current['brand']?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, [initialized, searchParams]);

  const handleReview = useCallback(() => {
    const allKeys: string[] = [
      ...STRATEGY_SECTIONS.map((s) => s.key).filter((k) => questionToSectionKeys.has(k)),
      'brand',
    ];
    const firstUnfilled = allKeys.find((k) => !confirmedSections.has(k));
    if (!firstUnfilled) return;
    setOpenSubSection(firstUnfilled);
    setTimeout(() => {
      subSectionHeaderRefs.current[firstUnfilled]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  }, [confirmedSections, questionToSectionKeys]);

  // ── Helpers ──
  const buildDraftFromFields = useCallback((): Phase1Data => {
    const draft: Phase1Data = JSON.parse(JSON.stringify(phase1));
    for (const f of FIELDS) {
      if (f.setValue && fields[f.key]) {
        f.setValue(draft, fields[f.key].value);
      }
    }
    draft.officeHours = officeHoursLocal;
    draft.membershipPlans = membershipPlansLocal;
    return draft;
  }, [phase1, fields, officeHoursLocal, membershipPlansLocal]);

  const setField = (key: string, patch: Partial<FieldState[string]>) => {
    setFields((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const confirmField = (key: string) => {
    setField(key, { confirmed: true, editing: false });
  };

  const confirmAll = async () => {
    // The server keeps a location's existing address over the website's, so
    // an address edited here must be saved to the location itself (as a
    // single-field confirm does) or Confirm All silently drops it.
    const address = (fields.address?.value ?? '').trim();
    if (address && address !== (canonical.structuredAddress?.full ?? '')) {
      try {
        await updateLocationMutation.mutateAsync(parseAddressString(address));
      } catch {
        return; // the mutation's own error toast explains it
      }
    }
    confirmMutation.mutate(buildDraftFromFields(), {
      onSuccess: () =>
        setFields((prev) => {
          const next = { ...prev };
          for (const k of Object.keys(next))
            next[k] = { ...next[k], confirmed: true, editing: false };
          return next;
        }),
    });
  };

  const updateProfileMutation = useMutation({
    mutationFn: (payload: Partial<LocationProfileInput>) =>
      locationsApi.patchProfile(locationId, payload),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsKeys.locationProfile(locationId), response);
      toastSuccess('Field saved');
    },
    onError: () => toastError('Failed to save profile field.'),
  });

  const updateLocationMutation = useMutation({
    mutationFn: (payload: Partial<LocationInput>) => locationsApi.update(locationId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.locations() });
      toastSuccess('Address saved');
    },
    onError: () => toastError('Failed to save address.'),
  });

  const confirmSingle = (key: string) => {
    if (key === 'officeHours') {
      setField(key, { value: formatOfficeHours(officeHoursLocal) });
    }

    // Mark the field confirmed only once its save succeeds; a failed save
    // leaves it unconfirmed (and Next disabled) instead of claiming success.
    const afterSave = {
      onSuccess: () => {
        confirmField(key);
        const allConfirmed = FIELDS.every((f) => f.key === key || fields[f.key]?.confirmed);
        if (allConfirmed) confirmMutation.mutate(buildDraftFromFields());
      },
    };

    if (key === 'officeHours') {
      updateProfileMutation.mutate(
        { officeHours: officeHoursLocal as Record<string, DayHours | null> },
        afterSave,
      );
    } else if (key === 'membershipPlans') {
      updateProfileMutation.mutate({ membershipPlans: membershipPlansLocal }, afterSave);
    } else if (key === 'paymentOptions') {
      const draft = buildDraftFromFields();
      updateProfileMutation.mutate({ paymentOptions: draft.paymentOptions ?? [] }, afterSave);
    } else if (['services', 'providers', 'technology', 'insurances', 'languages'].includes(key)) {
      const currentValue = fields[key]?.value ?? '';
      updateProfileMutation.mutate(
        { [key]: parseCSV(currentValue) } as Partial<LocationProfileInput>,
        afterSave,
      );
    } else if (key === 'address') {
      const value = (fields[key]?.value ?? '').trim();
      // An empty address is left as-is rather than wiping the location's address.
      if (value) updateLocationMutation.mutate(parseAddressString(value), afterSave);
      else afterSave.onSuccess();
    } else {
      afterSave.onSuccess();
    }
  };

  // Save the section's answers first; only a successful save marks (and
  // persists) the section as confirmed. A failed save leaves it unconfirmed.
  const commitSection = async (
    sectionKey: string,
    updates: Array<{ questionId: string; response: unknown }>,
  ) => {
    try {
      if (updates.length) await saveResponsesMutation.mutateAsync(updates);
      await saveConfirmedSectionsMutation.mutateAsync({ sections: [sectionKey] });
      setConfirmedSections((prev) => new Set([...prev, sectionKey]));
    } catch {
      toastError("Couldn't confirm this section. Please try again.");
    }
  };

  const confirmSection = (sectionKey: SectionKey, sectionQuestions: StrategyQuestion[]) => {
    const updates = dirtyUpdates(
      sectionQuestions.map((q) => q.id),
      responses,
      savedResponseMap,
    );
    void commitSection(sectionKey, updates);
  };

  const confirmBrandSection = () => {
    const updates = dirtyUpdates(
      yourBrandQuestions.map((q) => q.id),
      responses,
      savedResponseMap,
    );
    ARCHETYPE_Q_KEYS.forEach((key, i) => {
      const qId = archetypeQuestionIds[key];
      if (qId && picks[i] !== null && picks[i] !== savedResponseMap[qId]) {
        updates.push({ questionId: qId, response: picks[i] });
      }
    });
    void commitSection('brand', updates);
  };

  const brandFillStatus: 'filled' | 'partially-filled' | null = (() => {
    const brandAnswered = [
      ...yourBrandQuestions.map((q) => {
        const v = responses[q.id];
        return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
      }),
      ...picks.map((p) => p !== null),
    ];
    const count = brandAnswered.filter(Boolean).length;
    if (count === 0) return null;
    if (count === brandAnswered.length) return 'filled';
    return 'partially-filled';
  })();

  // ── Derived ──
  const section1FillCount = FIELDS.filter((f) => f.hasValue?.(canonical)).length;
  const section1FillStatus: 'filled' | 'partially-filled' | null =
    section1FillCount === FIELDS.length
      ? 'filled'
      : section1FillCount > 0
        ? 'partially-filled'
        : null;

  const sectionData = [
    { sec: STRATEGY_SECTIONS[0], questions: growthQuestions },
    { sec: STRATEGY_SECTIONS[1], questions: patientsQuestions },
    { sec: STRATEGY_SECTIONS[2], questions: budgetQuestions },
  ];

  // ── Loading / error state ──
  const loadError =
    extractionQuery.isError ||
    questionsQuery.isError ||
    responsesQuery.isError ||
    locationsQuery.isError ||
    locationProfileQuery.isError;
  if (loadError && !initialized) {
    return (
      <div className={styles.stepContainer}>
        <EmptyState
          title="Couldn't load your practice profile"
          description="Something went wrong loading this step. Try again in a moment."
          action={{
            label: 'Retry',
            onClick: () => {
              void extractionQuery.refetch();
              void questionsQuery.refetch();
              void responsesQuery.refetch();
              void locationsQuery.refetch();
              void locationProfileQuery.refetch();
            },
          }}
        />
      </div>
    );
  }

  const isLoading =
    extractionQuery.isLoading ||
    questionsQuery.isLoading ||
    responsesQuery.isLoading ||
    locationsQuery.isLoading ||
    locationProfileQuery.isLoading;
  if (isLoading || !initialized) {
    return (
      <div className={styles.loadingWrapper}>
        {[1, 2, 3].map((i) => (
          <div key={i} className={styles.skeleton} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <SectionLabel
        label="Practice Profile"
        sub="Confirm your practice details, then fill in your strategic inputs."
        timestamp={websiteRead ? extraction?.extractedAt : undefined}
      />

      {!websiteRead && (
        <Callout
          variant="info"
          title="We haven't read your website yet"
          actions={
            <Button
              size="sm"
              variant="primary"
              onClick={() => runExtractionMutation.mutate()}
              loading={runExtractionMutation.isPending}
              disabled={runExtractionMutation.isPending}
            >
              Read my website
            </Button>
          }
        >
          Pre-fill these details from your website, or enter them yourself below. The strategic
          questions don't depend on it.
        </Callout>
      )}

      <VapiCallModal
        isOpen={vapiModalOpen}
        onClose={() => setVapiModalOpen(false)}
        locationId={locationId}
        defaultPhone={currentLocation?.phone}
      />
      {(!section2Done || !brandSectionDone) && (
        <VapiCallBanner
          locationId={locationId}
          onOpen={() => setVapiModalOpen(true)}
          onReview={handleReview}
        />
      )}

      <PracticeDataSection
        fields={fields}
        setField={setField}
        confirmSingle={confirmSingle}
        confirmAll={confirmAll}
        confirmMutationPending={confirmMutation.isPending}
        officeHoursLocal={officeHoursLocal}
        setOfficeHoursLocal={setOfficeHoursLocal}
        membershipPlansLocal={membershipPlansLocal}
        setMembershipPlansLocal={setMembershipPlansLocal}
        confirmedCount={confirmedCount}
        totalFields={totalFields}
        section1Done={section1Done}
        section1FillStatus={section1FillStatus}
        section1Expanded={section1Expanded}
        setSection1Expanded={setSection1Expanded}
      />

      <StrategicInputsSection
        sectionData={sectionData}
        openSubSection={openSubSection}
        setOpenSubSection={setOpenSubSection}
        confirmedSections={confirmedSections}
        section1Done={section1Done}
        section2Done={section2Done}
        setSection1Expanded={setSection1Expanded}
        subSectionHeaderRefs={subSectionHeaderRefs}
        pendingScrollToKey={pendingScrollToKey}
        hasVapiCall={!!locationVapiCall}
        responses={responses}
        setResponse={setResponse}
        confirmSection={confirmSection}
      />

      <BrandPositioningSection
        yourBrandQuestions={yourBrandQuestions}
        picks={picks}
        togglePick={togglePick}
        responses={responses}
        setResponse={setResponse}
        confirmedSections={confirmedSections}
        brandSectionDone={brandSectionDone}
        brandFillStatus={brandFillStatus}
        openSubSection={openSubSection}
        setOpenSubSection={setOpenSubSection}
        section1Done={section1Done}
        setSection1Expanded={setSection1Expanded}
        subSectionHeaderRefs={subSectionHeaderRefs}
        pendingScrollToKey={pendingScrollToKey}
        confirmBrandSection={confirmBrandSection}
      />
    </div>
  );
}
